// packages/web/src/lib/attentionRoute.ts — R4 (Actionable Everywhere, WS2):
// resolves a Today attention item to WHERE it acts. Every attention tile is a
// DOORWAY (the Today-card taxonomy: a card is either an inline approve/cancel
// decision, or a doorway to the screen where you act) — nothing on Today is
// inert.
//
// The subject is already STRUCTURED on the read model: the attention row
// carries objectType + objectId (packages/server/src/plugins/attention.ts's
// serializeAttentionItem), so we resolve type -> destination directly and never
// have to parse the dedupKey string (e.g. `payment-overdue:<id>`). Two shapes
// of destination:
//   • 'editor' — open the schema-driven record editor on that exact record
//     (the overdue-payment example: openRecordEditor('actor_payment', id), the
//     same door CareerMoney's ledger rows use).
//   • 'navigate' — go to a record's own detail SCREEN (auditions/projects have
//     one) or, when a precise per-record destination isn't available, the
//     relevant list/section by item type (never inert).
// A tile whose subject we can't resolve at all returns null; Today then renders
// it as a plain, non-tappable tile (graceful fallback, no dead handle).
import { routePath } from '../routes/route';
import type { AttentionItem } from './api';

export type AttentionDestination =
  | { kind: 'editor'; typeKey: string; id: string }
  | { kind: 'navigate'; path: string };

// Record types with their OWN detail screen — a tile for one of these navigates
// to that screen (precise), rather than popping the editor overlay.
const DETAIL_SCREEN: Record<string, (id: string) => string> = {
  actor_audition: (id) => routePath({ screen: 'auditionDetail', id }),
  actor_project: (id) => routePath({ screen: 'projectDetail', id }),
};

// Record types the editor overlay can open directly (edit/act on the exact
// record) — the precise destination when there's no dedicated detail screen.
const EDITOR_TYPES = new Set([
  'actor_payment',
  'actor_sides',
  'actor_contract',
  'actor_person',
  'actor_session',
  'actor_shoot_day',
  'actor_review',
]);

// SECTION-LEVEL fallback by item type, for a subject we can't resolve to a
// specific record (no objectType, or a type with no editor/detail screen — e.g.
// a commitment). Routes to the surface the concern lives on, never nowhere.
const SECTION_BY_ITEM_TYPE: Record<string, string> = {
  overdue: routePath({ screen: 'career', section: 'money' }),
  due_soon: routePath({ screen: 'career', section: 'money' }),
  silence: routePath({ screen: 'auditions', stage: 'all' }),
  no_show: routePath({ screen: 'auditions', stage: 'all' }),
  drift: routePath({ screen: 'auditions', stage: 'all' }),
  booking_upcoming: routePath({ screen: 'auditions', stage: 'booked' }),
  instance_due: routePath({ screen: 'auditions', stage: 'all' }),
};

// ---------------------------------------------------------------------
// S7 (Part B) — the Rexx half of the table. Consulted ONLY when the caller
// says the tenant wears the Rexx pack (RexxHome passes 'rexx'); every
// existing caller passes nothing and Actor's maps above stay the whole
// story for them. Same doorway rule, Rexx vocabulary.
// ---------------------------------------------------------------------

// S4 built all four detail screens — precise doorways first.
const REXX_DETAIL_SCREEN: Record<string, (id: string) => string> = {
  hvac_work_order: (id) => routePath({ screen: 'workOrderDetail', id }),
  hvac_client: (id) => routePath({ screen: 'clientDetail', id }),
  hvac_site: (id) => routePath({ screen: 'siteDetail', id }),
  hvac_tech_profile: (id) => routePath({ screen: 'techDetail', id }),
};

// Rexx records with no detail screen of their own — the schema-driven
// editor is the door (same openRecordEditor path CareerMoney's ledger rows
// use on Actor). hvac_source is deliberately absent: a retained capture
// source is provenance, not a record the office edits.
const REXX_EDITOR_TYPES = new Set([
  'hvac_invoice',
  'hvac_payment',
  'hvac_receipt',
  'hvac_note',
  'hvac_material_line',
  'hvac_quote',
  'hvac_equipment',
  'hvac_visit',
  'hvac_rate',
]);

// Rexx section fallbacks — an unresolvable tile still lands on the surface
// the concern lives on. Money worries → the money screen; work-shaped
// worries → the work-order spine; visit-shaped ones → the schedule.
// malware_detected / scan_unavailable / parse_unavailable stay unmapped on
// BOTH skins (no surface owns them yet) and keep the documented inert
// fallback. ⚠ parse_unavailable names a retained aa_source, and a retained
// capture is provenance rather than a record a household edits — the same
// argument REXX_EDITOR_TYPES makes for leaving hvac_source out.
const REXX_SECTION_BY_ITEM_TYPE: Record<string, string> = {
  // S11: an ask with no readable subject still has somewhere to go — the
  // inbox itself.
  ask: routePath({ screen: 'asks', view: 'routed' }),
  overdue: routePath({ screen: 'money' }),
  due_soon: routePath({ screen: 'money' }),
  credential_expiring: routePath({ screen: 'techs' }),
  silence: routePath({ screen: 'workOrders' }),
  drift: routePath({ screen: 'workOrders' }),
  watcher: routePath({ screen: 'workOrders' }),
  instance_due: routePath({ screen: 'workOrders' }),
  low_stock: routePath({ screen: 'workOrders' }),
  booking_upcoming: routePath({ screen: 'schedule' }),
  no_show: routePath({ screen: 'schedule' }),
};

// ---------------------------------------------------------------------
// A3 — the actor_assist half. Same doorway rule, household vocabulary,
// consulted only when the caller says the tenant wears this pack.
//
// NO DETAIL-SCREEN TABLE YET, and its absence is deliberate rather than
// unfinished: this tranche ships the household home and the scope spine, and
// the pack's records reach their door through the SCHEMA-DRIVEN EDITOR —
// the same door CareerMoney's ledger rows have always used, which works off
// the record definition and needs no bespoke screen. When A3's audition and
// project spreads land they add themselves here as a detail table, exactly
// as Rexx's S4 drilldowns did, and every tile that already worked keeps
// working through the more precise door.
//
// aa_project, aa_role, aa_sides and aa_person ARE included. They are not
// "about a performer" — that is a scoping fact, not a reachability one — and
// an attention item naming a role still has to open somewhere.
const ACTOR_ASSIST_EDITOR_TYPES = new Set([
  'aa_subject',
  'aa_project',
  'aa_role',
  'aa_sides',
  'aa_person',
  'aa_audition',
  'aa_session',
  'aa_engagement',
  'aa_shoot_day',
  'aa_payment',
  'aa_expense',
  'aa_media',
  'aa_review',
  'aa_document',
  'aa_profile',
  'aa_training',
  'aa_award',
  'aa_skill',
]);

// Section fallbacks, onto the pack's OWN declared tabs (index.ts's navRoles:
// today · auditions · projects · people | money · documents · stats |
// materials · development). A worry with no readable subject still lands on
// the surface that owns it, never nowhere.
const ACTOR_ASSIST_SECTION_BY_ITEM_TYPE: Record<string, string> = {
  overdue: routePath({ screen: 'money' }),
  due_soon: routePath({ screen: 'money' }),
  silence: routePath({ screen: 'auditions', stage: 'all' }),
  no_show: routePath({ screen: 'auditions', stage: 'all' }),
  drift: routePath({ screen: 'auditions', stage: 'all' }),
  instance_due: routePath({ screen: 'auditions', stage: 'all' }),
  booking_upcoming: routePath({ screen: 'projects', phase: 'all' }),
  credential_expiring: routePath({ screen: 'documents' }),
};

/**
 * WHERE A RECORD IS ACTED ON — the record-type half of the table above, on
 * its own so that a caller holding a record (S11's asks screen: every ask
 * row is a doorway to the record it concerns) resolves through THE SAME
 * table Today does, instead of growing a second copy that drifts. Returns
 * null for a type with neither a detail screen nor an editor.
 */
export function resolveRecordDestination(
  recordType: string,
  id: string,
  vertical?: string | null
): AttentionDestination | null {
  const detailTable = vertical === 'rexx' ? REXX_DETAIL_SCREEN : DETAIL_SCREEN;
  const editorTypes =
    vertical === 'rexx'
      ? REXX_EDITOR_TYPES
      : vertical === 'actor_assist'
        ? ACTOR_ASSIST_EDITOR_TYPES
        : EDITOR_TYPES;
  const detail = detailTable[recordType];
  if (detail) return { kind: 'navigate', path: detail(id) };
  if (editorTypes.has(recordType)) return { kind: 'editor', typeKey: recordType, id };
  return null;
}

/**
 * Resolve an attention item to WHERE it acts. `vertical` names the pack the
 * tenant wears so the item-type tables can't cross skins ('rexx' consults
 * the Rexx tables; anything else keeps the original Actor behaviour,
 * byte-identical for existing callers).
 */
export function resolveAttentionDestination(
  item: AttentionItem,
  vertical?: string | null
): AttentionDestination | null {
  const { objectType, objectId } = item;
  if (vertical === 'rexx') {
    // S11: an ask is the one Needs-you subject that is NOT a record, so no
    // record-type table can place it — and its door is not a record's
    // screen but the answer itself. RexxHome folds asks routed to me into
    // the same list rather than starting a second attention concept
    // (client-side; nothing server-side produces them), and this is that
    // list's own resolver placing them.
    if (objectType === 'ask' && objectId) {
      return { kind: 'navigate', path: routePath({ screen: 'asks', view: 'routed', id: objectId }) };
    }
    if (objectType && objectId) {
      const record = resolveRecordDestination(objectType, objectId, 'rexx');
      if (record) return record;
    }
    // P-B's expiring ticket arrives as itemType 'credential_expiring' with
    // the LITERAL objectType 'record' (ops/ticketExpiry.ts), so the record-
    // type maps above can never resolve it — the item type routes it. Its
    // subject is the tech profile the ticket lives on, and that tech's
    // screen is where the office acts. No usable id → the Techs list;
    // never inert.
    if (item.itemType === 'credential_expiring') {
      if (objectType === 'record' && objectId) {
        return { kind: 'navigate', path: routePath({ screen: 'techDetail', id: objectId }) };
      }
      return { kind: 'navigate', path: routePath({ screen: 'techs' }) };
    }
    const rexxSection = REXX_SECTION_BY_ITEM_TYPE[item.itemType];
    if (rexxSection) return { kind: 'navigate', path: rexxSection };
    return null;
  }
  if (vertical === 'actor_assist') {
    if (objectType && objectId) {
      const record = resolveRecordDestination(objectType, objectId, 'actor_assist');
      if (record) return record;
    }
    const section = ACTOR_ASSIST_SECTION_BY_ITEM_TYPE[item.itemType];
    if (section) return { kind: 'navigate', path: section };
    return null;
  }
  if (objectType && objectId) {
    const record = resolveRecordDestination(objectType, objectId);
    if (record) return record;
  }
  const section = SECTION_BY_ITEM_TYPE[item.itemType];
  if (section) return { kind: 'navigate', path: section };
  return null;
}

/**
 * ⚑ Fix tranche 6, item 6 — THE SHARED HOME OF THE OPERATOR-VOICE RULE, and
 * why it does not live on a screen.
 *
 * Most attention `reason`s are already a good, specific, product-voiced
 * sentence ("Commitment 'X' is 3 days overdue") and rightly lead their tile.
 * The file-scanning trio is written for an AUDIT LOG instead — core's
 * ingestionPipeline.ts emits "Malware detected in uploaded file '…' ; file
 * quarantined — bytes retained as evidence, never processed or served", which
 * is the right sentence for an operator and the wrong one for a parent. For
 * those, the server's own short title (core's `attentionTitleFor` /
 * ATTENTION_TITLES, the same table an email subject reads) is what a household
 * member should see.
 *
 * ⚠ IT LIVES HERE BECAUSE THE FIRST FIX PUT IT ON THE WRONG SCREEN. Tranche 6
 * added this rule to `Today.tsx` — which the actor_assist pack never renders.
 * The pack renders `HouseholdHome.tsx`, so the walk on the deployed build still
 * showed the audit sentence verbatim, and the tranche's own test passed because
 * it tested the file that was edited. This codebase has now been bitten by that
 * exact shape twice (2026-08-26, B18g: the slippage read built onto `Today.tsx`
 * while the personal pack renders `CockpitToday`). A rule that two screens must
 * agree on is a rule that belongs in neither of them.
 */
export const OPERATOR_VOICED_REASON: ReadonlySet<string> = new Set([
  'malware_detected',
  'scan_unavailable',
  'parse_unavailable',
]);

/** The line a HOUSEHOLD MEMBER should read for one attention item. Both Today
 * and HouseholdHome call this; neither decides it alone. */
export function attentionLineFor(item: {
  itemType: string;
  reason?: string | null;
  title?: string | null;
}): string | null {
  if (OPERATOR_VOICED_REASON.has(item.itemType)) return item.title ?? item.reason ?? null;
  return item.reason ?? item.title ?? null;
}
